import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { snack } from '@innovamat/glimmer-components';
import type { GlowApiError } from '@innovamat/glow-api-client';
import { useTeachersQuery } from '@innovamat/glow-api-client';
import { useCopyToClipboard } from '@innovamat/hooks';
import type { ClassroomTeacher } from '../../../../modules/teacher';

import { useUser } from '../../../../../user-management';
import {
  ActionsDropdown,
  type CreateItem,
} from '../../../../components/common-table-components/actions-dropdown';
import { useOrganization } from '../../../../hooks/use-organization';
import { useResendInvitation } from '../../../../hooks/use-resend-invitation';

type ActionsCellProps = {
  classroomId: string;
  isPlatformEnabled: boolean;
  organizationId: string;
  teacher: ClassroomTeacher;
  handleLeaveClass: (teacher: ClassroomTeacher) => void;
  isLeaveDisabled?: boolean;
};

const ActionsCell = ({
  classroomId,
  isPlatformEnabled,
  organizationId,
  teacher,
  handleLeaveClass,
  isLeaveDisabled,
}: ActionsCellProps): JSX.Element | null => {
  const { t } = useTranslation();

  const [_, copy] = useCopyToClipboard();
  const { isRole } = useUser();
  const { isRosteringEnabled } = useOrganization();

  const queryClient = useQueryClient();

  const onSuccessAddTeachers = (): void => {
    const queryKey = useTeachersQuery.getKey({
      classroomId: classroomId ?? '',
      organizationId,
      isPlatformEnabled,
    });

    queryClient.refetchQueries({ queryKey });

    snack.success(t('teachers.resendInvitation.success'));
  };

  const onResendInvitationError = (error: GlowApiError): void => {
    snack.error(t('teachers.resendInvitation.error'));
  };

  const { isResendingInvitation, onResendInvitation } = useResendInvitation({
    onSuccess: onSuccessAddTeachers,
    onError: onResendInvitationError,
  });

  const isExistingTeacher =
    !teacher?.isSchoolInvitation && !teacher?.isInvitation;

  const items: CreateItem[] = useMemo(() => {
    if (isExistingTeacher) {
      return [
        {
          text: t('teachers.unlinkTeacher'),
          icon: 'LeaveClassIcon',
          state: isLeaveDisabled ? 'disabled' : 'active',

          onSelectItem: () => handleLeaveClass(teacher),
        },
      ];
    }

    if (!isExistingTeacher) {
      return [
        {
          text: t('teachers.resendInvitation'),
          icon: 'MailIcon',
          state: isResendingInvitation ? 'disabled' : 'active',
          onSelectItem: () => onResendInvitation(teacher),
        },
      ];
    }
    return [];
  }, [
    handleLeaveClass,
    isExistingTeacher,
    isRosteringEnabled,
    isResendingInvitation,
    onResendInvitation,
    t,
    teacher,
  ]);

  const copyItem: CreateItem = {
    icon: 'CopyIcon',
    onSelectItem() {
      copy(teacher?.id || '');
      snack.success(t('manager.table.copiedId'));
    },
    state: 'active',
    text: t('manager.table.copyId'),
  };

  const allItems = isRole.Advisor ? [...items, copyItem] : items;

  if (allItems.length === 0) {
    return null;
  }

  return <ActionsDropdown dropdownId="class-options-button" items={allItems} />;
};

export { ActionsCell };
